import React from "react";
import { useTheme } from "@mui/material/styles";
import { Box, ButtonBase, Typography, Stack, Divider, Tooltip, useMediaQuery } from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import LocalGasStationRoundedIcon from "@mui/icons-material/LocalGasStationRounded";
import HourglassBottomRoundedIcon from "@mui/icons-material/HourglassBottomRounded";
import { isWaitingForFunds } from "../../utils/botUtils";
import { IAssetData, IBotData } from "../../shared/types";
import {
  formatDateTime,
  formatPairPrice,
  formatSignedPp,
  formatSignedUsd,
  formatSignedPercent,
  formatUsdOrDash,
} from "../../utils/formatters";
import { AssetDistributionBar } from "../../components/AssetDistribuitionBar";
import { DetailBlock } from "../../components/cards/subcomponents/DetailBlock";
import EnhancedPriceRangeVisualizer from "../../components/PriceRangeVisualizer";
import { botActivity, balancingStatusMessage } from "../../utils/botActivity";
import { useAuth } from "../../hooks/useAuth";
import { SurfaceCard } from "../../components/ui/SurfaceCard";
import { StatusPill, StatusTone } from "../../components/ui/StatusPill";
import { MiniStat } from "../../components/ui/MiniStat";
import { Sparkline } from "../../components/ui/Sparkline";
import { chainOf } from "../../components/ui/ChainAvatar";
import { poolIndexTrend, processPortfolioHistory } from "../../features/overviewSeries";
import { tokens } from "../../theme";
import { BOT_STATE_LABEL, BOT_STATE_TONE, botState, bonusYieldUsd, capitalBasis, gasSeverity } from "../../utils/botMetrics";

const minutesSince = (ts?: number) =>
  ts && Number.isFinite(ts) ? Math.max(0, Math.floor((Date.now() - ts) / 60_000)) : null;

/** Summary amounts: whole dollars from $1,000 up, cents below; zero reads "—". */
const usd = formatUsdOrDash;

/** Step names arrive in capitals ("DERIBIT", "WAITING_FUNDS"): show them in sentence case ("Deribit"). */
const sentenceCase = (step: string) =>
  /[a-z]/.test(step) ? step : step.charAt(0) + step.slice(1).toLowerCase().replace(/_/g, " ");

// --- Bot card (family summary): state, performance, range and capital split ---
export const BotStatCard: React.FC<{ bot: IBotData; onSelect?: (botId: string) => void; id?: string }> = React.memo(
  ({ bot, onSelect, id }) => {
    const theme = useTheme();
    const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
    const [showCapital, setShowCapital] = React.useState(false);
    const { groups } = useAuth();
    const isAdmin = groups.includes("admin");
    const s = bot.stats;
    const balanceSnapshot = bot.stats.balanceSnapshotEnd;

    // --- State (shared definition: open AND price within its range = in range) ---
    const isPositionOpen = s.isPositionOpen;
    const state = botState(s);
    const isBalancing = state === "balancing";
    const statusLabel = BOT_STATE_LABEL[state];
    const statusTone: StatusTone = BOT_STATE_TONE[state];
    const cardTint =
      state === "in-range"
        ? (tokens.chainTint as Record<string, string>)[chainOf(s.botName)]
        : state === "balancing"
        ? tokens.gold
        : state === "disabled"
        ? undefined
        : theme.palette.error.main;
    const botNumber = s.botName.match(/_(\d+)$/)?.[1];
    const trend = React.useMemo(
      () => poolIndexTrend(processPortfolioHistory([bot])),
      [bot]
    );
    const netColor = s.pnlUSD < 0 ? theme.palette.error.main : theme.palette.success.main;

    const activity = botActivity(s);
    const activityMinutes = minutesSince(s.lastActivityAt ?? s.positionLastUpdated);
    const activityLabel =
      activity.state === "disabled"
        ? "Disabled in config"
        : activity.state === "unknown" || activityMinutes === null
        ? "Activity unknown"
        : activity.state === "recent"
        ? `Active ${activityMinutes}m ago`
        : `Last activity ${activityMinutes}m ago`;
    // Activity is information, not health: neutral while recent, amber once stale (never green).
    const activityTone: StatusTone = activity.state === "stale" || activity.state === "unknown" ? "warning" : "neutral";

    // Status pills, most severe first; two fit on the narrowest card, the rest fold into "+N".
    // Touch screens get no tooltips inside the tappable card (a near-miss would open the bot).
    const isTouch = useMediaQuery("(hover: none)");
    type Pill = { key: string; tone: StatusTone; label: string; icon?: React.ReactNode; pulsing?: boolean; tooltip?: string };
    const severityRank: Record<StatusTone, number> = { error: 0, warning: 1, brand: 2, success: 3, info: 4, neutral: 5 };
    const pills = (
      [
        { key: "state", tone: statusTone, label: statusLabel, pulsing: isBalancing },
        isAdmin && s.isLowGas === true && {
          key: "gas",
          tone: gasSeverity(s) as StatusTone,
          label: "Low gas",
          icon: <LocalGasStationRoundedIcon sx={{ fontSize: 13 }} />,
        },
        isAdmin && isWaitingForFunds(s) && {
          key: "funds",
          tone: "warning" as StatusTone,
          label: "Awaiting funds",
          icon: <HourglassBottomRoundedIcon sx={{ fontSize: 13 }} />,
        },
        { key: "activity", tone: activityTone, label: activityLabel, tooltip: activity.message },
      ].filter(Boolean) as Pill[]
    ).sort((a, b) => severityRank[a.tone] - severityRank[b.tone]);
    const shownPills = pills.slice(0, 2);
    const hiddenPills = pills.slice(2);

    // LP return minus HODL return, in percentage points (same figure as the backend's hodlDiffUSD / basis).
    const basis = capitalBasis(s);
    const vsHodl = basis > 0 ? s.hodlDiffUSD / basis : 0;

    // --- Tooltip logic for Balancing: the balancing token's amount, priced with the matching pair
    // token (wrapped/native aliases included: ETH ↔ WETH, RBTC ↔ WRBTC). Stablecoins or an
    // unknown token get no tooltip (the USD figure already says it all).
    let balancingTokenAmount = 0;
    let balancingTokenSymbol = "";
    const hideTooltipTokens = ["USDC", "USDT", "DOC", "USDRIF"];
    let showBalancingTooltip = true;
    const unwrap = (sym: string) => sym.trim().toUpperCase().replace(/^W(?=(ETH|BTC|RBTC|BNB|MATIC|AVAX)$)/, "");
    if (s.balancingUSD && balanceSnapshot) {
      const name = unwrap(s.balancingTokenName || "");
      if (name && name === unwrap(s.token0Symbol) && balanceSnapshot.token0Price > 0) {
        balancingTokenAmount = s.balancingUSD / balanceSnapshot.token0Price;
        balancingTokenSymbol = (s.balancingTokenName || s.token0Symbol).trim();
      } else if (name && name === unwrap(s.token1Symbol) && balanceSnapshot.token1Price > 0) {
        balancingTokenAmount = s.balancingUSD / balanceSnapshot.token1Price;
        balancingTokenSymbol = (s.balancingTokenName || s.token1Symbol).trim();
      }
    }

    if (
      (s.balancingTokenName &&
        hideTooltipTokens.includes(s.balancingTokenName.toUpperCase())) ||
      balancingTokenAmount === 0
    ) {
      showBalancingTooltip = false;
    }

    // Backend USD value only (amount × currentPrice would use the pair price, e.g. WETH per FET).
    const buybacksValue = s.repurchaseCurValue || 0;
    const capitalDetails = [
      {
        label: "Wallet",
        value: usd(s.balanceSnapshotEnd.walletValueUSD),
      },
      {
        label: "Pool",
        value: usd(s.balanceSnapshotEnd.poolValueUSD),
      },
      // Uncollected fees are already inside the pool value (liquidity + fees): a breakdown, not an addend.
      { label: "of which uncollected fees", value: usd(s.accruedFeesUSD || 0), indent: true },
      {
        label: "Balancing",
        value: showBalancingTooltip ? (
          <Tooltip
            title={`≈ ${balancingTokenAmount.toFixed(
              2
            )} ${balancingTokenSymbol}`}
            placement="top"
          >
            <Box
              component="span"
              data-tooltip-trigger
              sx={{ cursor: "help", borderBottom: "1px dotted", borderColor: "text.disabled" }}
            >
              {usd(s.balancingUSD)}
            </Box>
          </Tooltip>
        ) : (
          <span>{usd(s.balancingUSD)}</span>
        ),
      },
      // Lending parks part of the balancing surplus: a breakdown of the row above, not an addition.
      { label: "of which lent", value: usd(s.lentUSD), indent: true },
      {
        label: "Buybacks value",
        value: usd(buybacksValue),
      },
    ].filter((row) => {
      // Rows at $0 add noise: only show the places that hold value.
      if (row.label === "Wallet") return (s.balanceSnapshotEnd.walletValueUSD || 0) > 0;
      if (row.label === "Pool") return (s.balanceSnapshotEnd.poolValueUSD || 0) > 0;
      if (row.label === "Balancing") return (s.balancingUSD || 0) > 0;
      if (row.label === "of which lent") return (s.lentUSD || 0) > 0;
      if (row.label === "Buybacks value") return buybacksValue > 0;
      if (row.label === "of which uncollected fees") return (s.accruedFeesUSD || 0) > 0;
      return true;
    });

    // Token split of everything the bot holds on-chain (wallet + pool), the same basis as the
    // detail view's "Total tokens"; hidden when nothing is on-chain (e.g. fully balancing).
    const token0Amount = (balanceSnapshot?.walletToken0 || 0) + (balanceSnapshot?.poolToken0 || 0);
    const token1Amount = (balanceSnapshot?.walletToken1 || 0) + (balanceSnapshot?.poolToken1 || 0);
    const token0USD = token0Amount * (balanceSnapshot?.token0Price || 0);
    const token1USD = token1Amount * (balanceSnapshot?.token1Price || 0);
    const assetData: IAssetData[] =
      balanceSnapshot && token0USD + token1USD > 0
        ? [
            { symbol: s.token0Symbol, valueUSD: token0USD, amount: token0Amount, color: theme.palette.chart.gold },
            { symbol: s.token1Symbol, valueUSD: token1USD, amount: token1Amount, color: theme.palette.chart.green },
          ]
        : [];

    const currentStrike: number | null = s.currentStrike;
    const nextExpiration: string | null = s.nextExpiration;
    const extraYield = bonusYieldUsd(s);

    const interactive = Boolean(onSelect);

    return (
      <SurfaceCard
        id={id}
        tint={cardTint}
        radius="lg"
        padding="none"
        interactive={interactive}
        role={interactive ? "button" : undefined}
        tabIndex={interactive ? 0 : undefined}
        aria-label={interactive ? `Open ${s.botName}` : undefined}
        onClick={
          interactive
            ? (e: React.MouseEvent) => {
                // Let tooltips / links inside the card keep their own clicks.
                if ((e.target as HTMLElement).closest("a, button, [data-tooltip-trigger]")) return;
                onSelect?.(s.botName);
                window.scrollTo({ top: 0 });
              }
            : undefined
        }
        onKeyDown={
          interactive
            ? (e: React.KeyboardEvent) => {
                if (e.key === "Enter") onSelect?.(s.botName);
              }
            : undefined
        }
        sx={{ height: "100%", display: "flex", flexDirection: "column", scrollMarginTop: { xs: 168, md: 108 } }}
      >
        <Box sx={{ px: { xs: 2.5, md: 3 }, pt: { xs: 2.5, md: 3 } }}>
          {/* Header */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: { xs: "1rem", md: "1.0625rem" }, fontWeight: 700, letterSpacing: "-0.01em" }} noWrap>
                {botNumber ? `Bot ${botNumber}` : s.botName}
              </Typography>
              {botNumber && (
                <Typography noWrap sx={{ fontSize: "0.75rem", color: "text.secondary", mt: 0.25 }}>
                  {s.botName}
                  {isAdmin && s.isPositionOpen && s.roleIndex !== undefined ? ` · Role ${s.roleIndex}` : ""}
                </Typography>
              )}
            </Box>
            {interactive && (
              <ChevronRightRoundedIcon sx={{ color: "text.disabled", fontSize: 20, mr: -0.5 }} />
            )}
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1, flexWrap: "wrap", rowGap: 0.75 }}>
            {shownPills.map((p) =>
              p.tooltip && !isTouch ? (
                <Tooltip key={p.key} title={p.tooltip} placement="bottom-start">
                  <Box component="span" data-tooltip-trigger sx={{ display: "inline-flex" }}>
                    <StatusPill tone={p.tone} label={p.label} size="sm" icon={p.icon} pulsing={p.pulsing} />
                  </Box>
                </Tooltip>
              ) : (
                <StatusPill key={p.key} tone={p.tone} label={p.label} size="sm" icon={p.icon} pulsing={p.pulsing} />
              )
            )}
            {hiddenPills.length > 0 &&
              (isTouch ? (
                <StatusPill tone="neutral" size="sm" dot={false} label={`+${hiddenPills.length}`} />
              ) : (
                <Tooltip title={hiddenPills.map((p) => p.label).join(" · ")} placement="bottom-start">
                  <Box component="span" data-tooltip-trigger sx={{ display: "inline-flex" }}>
                    <StatusPill tone="neutral" size="sm" dot={false} label={`+${hiddenPills.length}`} />
                  </Box>
                </Tooltip>
              ))}
          </Stack>

          {/* Performance */}
          <Stack direction="row" alignItems="flex-end" justifyContent="space-between" spacing={2} sx={{ mt: 2.25 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                component="div"
                sx={{
                  fontSize: { xs: "1.5rem", md: "1.75rem" },
                  fontWeight: 700,
                  letterSpacing: "-0.03em",
                  lineHeight: 1.05,
                  color: netColor,
                  whiteSpace: "nowrap",
                }}
              >
                {/* No meaningful % when more was withdrawn than put in (basis ≤ 0). */}
                {basis > 0 ? formatSignedPercent(s.pnlPerCapital) : "—"}
              </Typography>
              <Typography component="div" noWrap sx={{ fontSize: "0.78125rem", color: "text.secondary", mt: 0.5 }}>
                {formatSignedUsd(s.pnlUSD)} net
              </Typography>
            </Box>
            {isAdmin && (
              <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                <Typography component="div" sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                  vs HODL
                </Typography>
                <Typography
                  component="div"
                  sx={{
                    fontSize: "1rem",
                    fontWeight: 700,
                    letterSpacing: "-0.01em",
                    color: vsHodl < 0 ? "error.main" : "success.main",
                    whiteSpace: "nowrap",
                  }}
                >
                  {basis > 0 ? formatSignedPp(vsHodl) : "—"}
                </Typography>
                <Typography component="div" noWrap sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                  {formatSignedUsd(s.hodlDiffUSD)}
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>

        {/* LP index trend, edge to edge */}
        <Box sx={{ mt: 1.25 }}>
          {trend.length > 1 ? <Sparkline data={trend} color={netColor} height={48} fill dot /> : <Box sx={{ height: 12 }} />}
        </Box>

        <Box
          sx={{
            px: { xs: 2.5, md: 3 },
            pb: { xs: 2.5, md: 3 },
            pt: 1.5,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 2.25,
          }}
        >
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: { xs: 1.5, sm: 1 } }}>
          <MiniStat label="Capital" value={s.totalCapitalUSD} flat={isPhone} />
          <MiniStat label="Fees" value={s.totalPoolFeesUSDOrig} flat={isPhone} />
          <MiniStat label="Bonus yield" value={extraYield} flat={isPhone} />
        </Box>

        {/* Range or balancing state. In a multi-column grid both variants take the same height, so the
            sections below line up across cards. */}
        <Box sx={{ display: "flex", flexDirection: "column", minHeight: { sm: 136 }, "& > *": { flex: { sm: 1 } } }}>
        {isPositionOpen ? (
          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <ShowChartRoundedIcon sx={{ fontSize: 15, color: "text.secondary" }} />
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "text.secondary" }}>
                  Price range
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                Now{" "}
                <Box component="span" sx={{ color: "text.primary", fontWeight: 650 }}>
                  {formatPairPrice(s.poolPrice ?? s.currentPrice, s.token0Symbol, s.token1Symbol, s.decimals)}
                </Box>
              </Typography>
            </Stack>
            <EnhancedPriceRangeVisualizer
              min={s.rangeLower}
              max={s.rangeUpper}
              current={s.poolPrice ?? s.currentPrice}
              entry={s.entryPrice}
              decimals={s.decimals}
              formatPrice={(v) => formatPairPrice(v, s.token0Symbol, s.token1Symbol, s.decimals)}
            />
          </Box>
        ) : (
          <SurfaceCard inset padding="sm" sx={{ textAlign: "left" }}>
            <Typography sx={{ fontSize: "0.8125rem", lineHeight: 1.5 }}>
              {balancingStatusMessage(s)}
            </Typography>
            {nextExpiration && currentStrike ? (
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                Option expiry {nextExpiration} · Strike {currentStrike}
              </Typography>
            ) : null}
            {s.positionLastUpdated && (
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                Position data updated {formatDateTime(s.positionLastUpdated)}
              </Typography>
            )}
            {isAdmin && s.currentBalancingStep && (
              <Typography
                variant="caption"
                component="div"
                sx={{ mt: 0.75, color: "text.secondary", fontWeight: 600, display: "flex", alignItems: "center", gap: 0.5 }}
              >
                <SyncRoundedIcon sx={{ fontSize: 14, color: tokens.gold }} aria-hidden />
                Step {sentenceCase(s.currentBalancingStep)}
                {s.currentBalancingSubStep && ` → ${s.currentBalancingSubStep}`}
              </Typography>
            )}
          </SurfaceCard>
        )}
        </Box>

        <Divider sx={{ borderColor: tokens.borderSubtle }} />

        {/* Phones: capital breakdown folds away (keeps the bot list scannable) */}
        {isPhone && (
          <ButtonBase
            data-tooltip-trigger
            onClick={() => setShowCapital((v) => !v)}
            aria-expanded={showCapital}
            sx={{ justifyContent: "space-between", width: "100%", minHeight: 44, mt: -1, mb: showCapital ? -1 : -1.5, borderRadius: "12px" }}
          >
            <Stack direction="row" spacing={0.75} alignItems="center">
              <AccountBalanceWalletRoundedIcon sx={{ fontSize: 15, color: "text.secondary" }} />
              <Typography sx={{ fontSize: "0.8125rem", fontWeight: 650, color: "text.secondary" }}>
                Capital distribution
              </Typography>
            </Stack>
            <ExpandMoreRoundedIcon
              sx={{ fontSize: 20, color: "text.secondary", transition: "transform 200ms ease", transform: showCapital ? "rotate(180deg)" : "none" }}
            />
          </ButtonBase>
        )}

        {(!isPhone || showCapital) && (
          <>
            {!isPhone && (
              <DetailBlock
                title="Capital distribution"
                icon={AccountBalanceWalletRoundedIcon}
                items={capitalDetails}
              />
            )}
            {isPhone && <DetailBlock title="" items={capitalDetails} />}

            {assetData.length > 0 && (
              // In a grid, pinned to the card's foot so the bars line up across cards of different content.
              <Box sx={{ mt: { sm: "auto" } }}>
                <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", mb: 1 }}>
                  Token split ·{" "}
                  {(balanceSnapshot?.walletValueUSD || 0) > 0 && (balanceSnapshot?.poolValueUSD || 0) > 0
                    ? "wallet + pool"
                    : (balanceSnapshot?.poolValueUSD || 0) > 0
                    ? "pool"
                    : "wallet"}
                </Typography>
                <AssetDistributionBar assets={assetData} />
              </Box>
            )}
          </>
        )}
        </Box>
      </SurfaceCard>
    );
  }
);
